import "./UserEditForm.scss"
import Form, { EmailRule, Label, RequiredRule, SimpleItem, GroupItem, PatternRule } from "devextreme-react/form";
import { useNavigate } from "react-router-dom";
import { useGetCompanyUser } from "../api/get-user"
import { Item, Toolbar } from "devextreme-react/toolbar";
import { List } from "devextreme-react/list";
import { Button } from "devextreme-react/button";
import { useEffect, useState } from "react";
import { CompanyUserDetails } from "../types/CompanyUserDetails";
import { useUpdateCompanyUser } from "../api/update-user";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { useCreateCompanyUser } from "../api/create-user";
import { useResetCompanyUserPassword } from "../api/reset-password";
import { alert, confirm } from 'devextreme/ui/dialog';
import { LoadPagePanel } from "../../../../components";


export default function UserEditForm(params: { userName?: string }) {

	const [formState, setFormState] = useState<CompanyUserDetails>({} as CompanyUserDetails);
	const isNewUser = !params.userName;

	const isProjectManagerSelected = formState.groups?.includes('Project Manager');

	const getUser = useGetCompanyUser(params.userName);
	console.log(params.userName);

	const updateUser = useUpdateCompanyUser(formState,
		() => {
			showSuccessMessage("User has been updated successfully");
			navigate('/cfi/users');
		},
		(error) => {
			showErrorMessage(error);
		})

	const createUser = useCreateCompanyUser(formState,
		() => {
			showSuccessMessage("User has been created successfully");
			navigate('/cfi/users');
		},
		(error) => {
			showErrorMessage(error);
		})

	const resetPassword = useResetCompanyUserPassword(formState.userName,
		() => {
			alert("A password reset link has been sent to the user", "Success")
				.then(() => navigate('/cfi/users'));
		},
		(error) => {
			showErrorMessage(error);
		})

	const navigate = useNavigate();

	useEffect(() => {
		if (getUser.data?.details)
			setFormState(getUser.data?.details);
	}, [getUser.data]);


	return (
		<div className="dx-card user-form">
			<Form
				formData={formState}
				colCount={2}
				labelLocation="left"
				validationGroup="userValidationGroup"
				onFieldDataChanged={(e) => {

					let userName = formState.userName;
					if (isNewUser) {
						if (e.dataField === "firstName" && e.value && formState.lastName) {
							userName = e.value + '.' + formState.lastName
						}
						else if (e.dataField === "lastName" && e.value && formState.firstName) {
							userName = formState.firstName + '.' + e.value
						}

						if (userName !== formState.userName) {
							setFormState({ ...formState, userName: userName });
						}
					}

					if (e.dataField === "inactive" && e.value === true) {
						setFormState({
							...formState,
							emailNotifications: []
						});
					}
				}}
			>
				<SimpleItem
					dataField="firstName"
					isRequired={true}
				/>

				<SimpleItem
					dataField="lastName"
					isRequired={true}
				/>

				<SimpleItem
					dataField="userName"
					isRequired={isNewUser}
					editorOptions={{
						readOnly: !isNewUser
					}}
				>
					<PatternRule pattern={'^[a-zA-Z0-9._-]+$'} message="Only letters, digits, dots, underscores, and gyphens are allowed" />
				</SimpleItem>

				<SimpleItem
					dataField="inactive"
					editorType="dxCheckBox"
				/>

				<SimpleItem
					dataField="groups"
					isRequired={true}
					cssClass="checkbox-list"
					render={() => {
						return (
							<List
								height={180}
								pageLoadMode="scrollBottom"
								dataSource={getUser.data?.groups}
								keyExpr="groupName"
								displayExpr="groupName"
								searchExpr="groupName"
								showSelectionControls={true}
								selectionMode="multiple"
								selectedItemKeys={formState.groups}
								selectByClick={true}
								searchEnabled={true}
								onSelectionChanged={
									(args) => {
										setFormState({
											...formState,
											groups: args.component.option("selectedItems")?.map(x => x.groupName) ?? []
										});
									}
								}
							/>
						)
					}}
				>
					<Label text="User Roles" />
				</SimpleItem>
				<SimpleItem dataField="emailNotifications"
					cssClass="checkbox-list"
					render={() => {
						return (
							<List
								height={180}
								pageLoadMode="scrollBottom"
								dataSource={getUser.data?.notifications}
								keyExpr="notificationTemplateCode"
								displayExpr="notificationTemplateName"
								searchExpr="notificationTemplateName"
								showSelectionControls={true}
								selectionMode="all"
								selectedItemKeys={formState.emailNotifications}
								selectByClick={true}
								searchEnabled={true}
								onSelectionChanged={
									(args) => {
										setFormState({
											...formState,
											emailNotifications: args.component.option("selectedItems")?.map(x => x.notificationTemplateCode) ?? []
										});
									}
								}
							/>
						)
					}} />

				<GroupItem colCount={2} colSpan={1}>
					<SimpleItem dataField="phone" isRequired={true} cssClass="phone-item"
						editorOptions={{
							mask: 'X00-000-0000',
							maskRules: {
								X: /[02-9]/,
							},
							useMaskedValue: true,
							showMaskMode: 'onFocus',
						}}>
					</SimpleItem>
					<SimpleItem dataField="phoneExtension"
						editorOptions={{
							mask: '0999999999',
							maxLength: 10,
							maskRules: {
								'0': /\d/,
							},
							showMaskMode: 'onFocus',
						}}>
					</SimpleItem>
				</GroupItem>

				<GroupItem>
					<SimpleItem dataField="emailAddress" isRequired={true}>
						<RequiredRule />
						<EmailRule />
					</SimpleItem>
				</GroupItem>
				{
					isProjectManagerSelected &&
					<SimpleItem dataField="stores"
						cssClass="checkbox-list"
						isRequired={true}
						render={() => {
							return (
								<List
									height={180}
									pageLoadMode="scrollBottom"
									dataSource={getUser.data?.stores}
									keyExpr="companyID"
									displayExpr={(item) => {
										return item.companyCode + ' - ' + item.companyName;
									}}
									searchExpr={["companyCode", "companyName"]}
									showSelectionControls={true}
									selectionMode="all"
									selectedItemKeys={formState.stores}
									selectByClick={true}
									searchEnabled={true}
									onSelectionChanged={
										(args) => {
											setFormState({
												...formState,
												stores: args.component.option("selectedItems")?.map(x => x.companyID) ?? []
											});
										}
									}
								/>
							)
						}} >
					</SimpleItem>
				}
				{
					isProjectManagerSelected &&
					<SimpleItem dataField="productLines"
						cssClass="checkbox-list"
						isRequired={true}
						render={() => {
							return (
								<List
									height={180}
									pageLoadMode="scrollBottom"
									dataSource={getUser.data?.productLines}
									keyExpr="productLineID"
									displayExpr={"description"}
									searchExpr={"description"}
									showSelectionControls={true}
									selectionMode="all"
									selectedItemKeys={formState.productLines}
									selectByClick={true}
									searchEnabled={true}
									onSelectionChanged={
										(args) => {
											setFormState({
												...formState,
												productLines: args.component.option("selectedItems")?.map(x => x.productLineID) ?? []
											});
										}
									}
								>
								</List>
							)
						}} >
					</SimpleItem>
				}
				<SimpleItem
					dataField="morawareAssigneeID"
					editorType="dxSelectBox"
					editorOptions={{
						dataSource: getUser.data?.morawareAssignees,
						valueExpr: 'morawareAssigneeID',
						displayExpr: 'assigneeName',
						showClearButton: true
					}}
				>
					<Label text="Moraware Assignee" />
				</SimpleItem>
			</Form>

			<Toolbar className="button-toolbar">

				<Item location="before" visible={!isNewUser}>
					<Button
						text="Reset Password"
						onClick={(e) => {
							confirm("Are you sure you want to reset password for this user?", "Reset Password")
								.then((result) => {
									if (result) {
										resetPassword.mutate();
									}
								});
						}}
					></Button>
				</Item>

				<Item location="after">
					<Button
						text="Save"
						useSubmitBehavior={true}
						validationGroup="userValidationGroup"
						onClick={(e) => {

							let isValid = e.validationGroup.validate().isValid;
							if (!formState.groups || formState.groups.length === 0) {
								showErrorMessage("At least one role must be selected");
								isValid = false;
							}

							if (isProjectManagerSelected && (!formState.stores || formState.stores.length === 0)) {
								showErrorMessage("At least one store must be selected");
								isValid = false;
							}

							if (isProjectManagerSelected && (!formState.productLines || formState.productLines.length === 0)) {
								showErrorMessage("At least one product line must be selected");
								isValid = false;
							}

							if (isValid) {
								if (isNewUser)
									createUser.mutate();
								else
									updateUser.mutate();
							}
						}}
					></Button>
				</Item>
				<Item location="after">
					<Button
						text="Cancel"
						onClick={() => navigate('/cfi/users')}
					></Button>
				</Item>
			</Toolbar>
			<LoadPagePanel visible={getUser.isLoading || createUser.isLoading || updateUser.isLoading || resetPassword.isLoading} />
		</div>
	)
}
